import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getCateringOrderDetailsAsync } from "../../redux/features/cateringCustomer/cateringCustomerThunks";
import {
  selectCateringOrderDetails,
} from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import { selectCateringPackages, selectPendingPayment } from "../../redux/features/restaurants/restaurantSelectors";
import { 
  setCateringCartFilters, 
  addCateringCartPackage, 
  setCateringRestaurantId,
  clearCateringCart,
  addItemToCateringPackage,
  setCateringItemsToRestore,
  clearCateringItemsToRestore,
  setIsPastOrderRestoration,
  setModifyOrderMode,
  clearModifyOrderMode,
  setPastOrderAddressId,
  clearPastOrderAddressId,
  clearPendingPayment
} from "../../redux/features/restaurants/restaurantSlice";
import { getCateringPackagesAsync } from "../../redux/features/restaurants/restaurantThunks";
import { setSelectedAddress, setCateringOrderDetails } from "../../redux/features/cateringCustomer/cateringCustomerSlice";
import { setCompanySelectedAddress } from "../../redux/features/company/companySlice";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import WarningModal from "../modal";
import CateringOrderDetailsModal from "../modal/CateringOrderDetailsModal";

const CateringPastOrderCard = ({
  restaurant,
  serviceItemName,
  date,
  orderStatus,
  notes,
  orderId,
  orderNo,
  totalPrice,
  currency = "KWD",
  // Additional props for navigation and cart restoration
  serviceItemId,
  restaurantId,
  noOfPax,
  serviceItemDate,
  serviceItemSlot,
  serviceItems = [],
  customerName,
  orderDate,
  // Props for modification tracking
  updatedByAgentId = "",
  updatedDatetime = "",
}) => {
  const isReorderDisabled = [
    "Ready for Dispatch",
    "Preparing",
    "On the way",
    "Submitted",
    "Accepted",
  ].includes(orderStatus);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [showPendingPaymentWarning, setShowPendingPaymentWarning] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isLoadingModalDetails, setIsLoadingModalDetails] = useState(false);
  
  // Check if order has been modified
  const isModified = updatedByAgentId && updatedByAgentId.trim() !== "";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const cateringOrderDetails = useSelector(selectCateringOrderDetails);
  const cateringPackages = useSelector(selectCateringPackages);
  const serviceType = useSelector(selectServiceType);
  const pendingPayment = useSelector(selectPendingPayment);

  // Define button styles based on the status
  const getButtonStyle = (status) => {
    switch (status) {
      case "Submitted":
        return "bg-[#F7B500] text-white";
      case "Accepted":
        return "bg-[#02BBD5] text-white";
      case "Canceled":
        return "bg-[#E02020] text-white";
      case "Ready for Dispatch":
        return "bg-[#F7B500] text-white";
      case "Preparing":
        return "bg-[#0091FF] text-white";
      case "On the way":
        return "bg-[#6236FF] text-white";
      case "Delivered":
        return "bg-[#4E948A] text-white";
      default:
        return "bg-gray-400 text-black";
    }
  };

  const handleErrorClose = () => {
    setShowErrorModal(false);
    setIsLoadingOrderDetails(false);
  };

  const handleViewDetails = () => {
    // For "Pending Payment" orders, navigate to package detail with cart restoration
    if (orderStatus === "Pending Payment") {
      // First fetch the complete order details, then restore cart
      if (
        cateringOrderDetails &&
        Object.keys(cateringOrderDetails).length > 0 &&
        (cateringOrderDetails.id === orderId || cateringOrderDetails.order_no === orderNo)
      ) {
        handlePendingPaymentOrder();
      } else {
        // If we don't have order details yet, the useEffect will handle it after fetching
        console.log('Order details not available, will be handled after fetch');
      }
    } else if (orderStatus === "Paid") {
      // For paid orders, enable modify mode (date/time editable, PAX frozen, no add-ons)
      if (
        cateringOrderDetails &&
        Object.keys(cateringOrderDetails).length > 0 &&
        (cateringOrderDetails.id === orderId || cateringOrderDetails.order_no === orderNo)
      ) {
        handleModifyOrder();
      } else {
        // If we don't have order details yet, the useEffect will handle it after fetching
        console.log('Order details not available, will be handled after fetch');
      }
    } else if (orderStatus !== "Canceled") {
      // For other non-canceled orders, continue payment (restore cart like pending payment)
      if (
        cateringOrderDetails &&
        Object.keys(cateringOrderDetails).length > 0 &&
        (cateringOrderDetails.id === orderId || cateringOrderDetails.order_no === orderNo)
      ) {
        handlePendingPaymentOrder(); // Same behavior as pending payment - restore cart
      } else {
        // If we don't have order details yet, the useEffect will handle it after fetching
        console.log('Order details not available, will be handled after fetch');
      }
    } else {
      // For canceled orders, show details or navigate to order details page
      if (cateringOrderDetails && Object.keys(cateringOrderDetails).length > 0) {
        console.log('Catering Order Details:', cateringOrderDetails);
        // TODO: Navigate to catering order details page when implemented
        // navigate("/catering-order-details");
      } else {
        setShowErrorModal(true);
      }
    }
  };

  const handleModifyOrder = async () => {
    try {
      console.log('Starting modify order mode with order details:', cateringOrderDetails);
      
      // Use the detailed order data from API
      const orderData = cateringOrderDetails;
      
      // Pass modify order data to handleOrderRestoration
      // It will set the flags AFTER clearing the cart
      // Parse the original total from order details
      const originalTotal = parseFloat(orderData.total_price || totalPrice || 0);
      
      await handleOrderRestoration(orderData, true, {
        modifyOrderPax: parseInt(orderData.no_of_pax || noOfPax),
        modifyOrderId: orderId,
        modifyOrderOriginalTotal: originalTotal
      });
    } catch (error) {
      console.error('Error setting up modify order mode:', error);
      setShowErrorModal(true);
    }
  };

  const handlePendingPaymentOrder = async () => {
    try {
      console.log('Starting cart restoration with order details:', cateringOrderDetails);
      
      // Use the detailed order data from API
      const orderData = cateringOrderDetails;
      
      // Not in modify mode for pending payment orders (pass null for modifyModeData)
      // clearModifyOrderMode will be called automatically by clearCateringCart
      await handleOrderRestoration(orderData, false, null);
    } catch (error) {
      console.error('Error restoring pending payment order:', error);
      setShowErrorModal(true);
    }
  };

  const handleOrderRestoration = async (orderData, isModifyMode, modifyModeData = null) => {
    try {
      console.log('🚀 ============================================');
      console.log('🚀 Starting order restoration for Order #' + (orderData.order_no || orderNo));
      console.log('🚀 Order ID:', orderData.id || orderId);
      console.log('🚀 Is Modify Mode:', isModifyMode);
      console.log('🚀 Modify Mode Data:', modifyModeData);
      console.log('🚀 ============================================');
      
      // IMPORTANT: Clear ALL Redux flags and cart data FIRST before any operations
      console.log('🧹 Step -1: Explicitly clearing itemsToRestore from Redux');
      dispatch(clearCateringItemsToRestore());
      
      console.log('🧹 Step -2: Clearing entire catering cart (includes all restoration flags in Redux)');
      dispatch(clearCateringCart()); // This also clears isPastOrderRestoration and pastOrderAddressId
      dispatch(setCateringCartFilters({
        pax: 20,
        date: "",
        time: ""
      }));
      
      // Step -3: Set modify mode flags AFTER clearing (if this is modify mode)
      if (isModifyMode && modifyModeData) {
        console.log('🔒 Step -3: Setting modify mode flags in Redux AFTER cart clear');
        console.log('🔒 Original order total:', modifyModeData.modifyOrderOriginalTotal);
        dispatch(setModifyOrderMode({
          isModifyOrderMode: true,
          modifyOrderPax: modifyModeData.modifyOrderPax,
          modifyOrderId: modifyModeData.modifyOrderId,
          modifyOrderOriginalTotal: modifyModeData.modifyOrderOriginalTotal
        }));
      }
      
      // IMPORTANT: Fetch catering packages first to populate Redux
      // This is needed so the package detail page can find the selectedPackage
      // Categories will be fetched by the package detail page itself
      const targetRestaurantId = orderData.restaurant_id || restaurantId;
      const targetPackageId = orderData.service_item_id || serviceItemId;
      
      console.log('Step 0: Fetching catering packages for restaurant:', targetRestaurantId);
      const fetchedPackages = await dispatch(getCateringPackagesAsync(targetRestaurantId, "", "en", 3));
      
      // Get the full package data from the fetched response
      // The thunk returns the response directly (see restaurantThunks.js line 304)
      // This ensures we have ALL the data: pricing_tiers, categories, choose_rule, etc.
      const packagesData = fetchedPackages?.payload || fetchedPackages;
      const fullPackageData = packagesData?.find?.(pkg => pkg.id === targetPackageId);
      
      if (!fullPackageData) {
        console.error('Could not find package data after fetching:', targetPackageId, 'Fetched packages:', packagesData);
        setShowErrorModal(true);
        return;
      }
      
      console.log('Full package data retrieved:', fullPackageData);
      
      // Set restaurant ID
      dispatch(setCateringRestaurantId(targetRestaurantId));
      
      // Set address from past order if available
      if (orderData.address_id) {
        // Store the address ID in Redux for later use when customer/company data is loaded
        dispatch(setPastOrderAddressId(parseInt(orderData.address_id)));
        console.log('Stored past order address ID in Redux for later restoration:', orderData.address_id);
      }
      
      // Set filters based on detailed order data
      // Convert various date formats to YYYY-MM-DD format for date input
      const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        
        try {
          // Check if the date is already in YYYY-MM-DD format
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
          }
          
          // Handle DD-MM-YYYY format from API
          if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('-');
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
          
          // Handle timestamp or other formats - try to parse as Date
          const parsedDate = new Date(dateString);
          if (!isNaN(parsedDate.getTime())) {
            const year = parsedDate.getFullYear();
            const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
            const day = String(parsedDate.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
          
          // If all else fails, return empty string
          console.warn('Unable to format date:', dateString);
          return "";
        } catch (error) {
          console.error('Error formatting date:', error, 'Date string:', dateString);
          return "";
        }
      };

      const filters = {
        pax: parseInt(orderData.no_of_pax) || parseInt(noOfPax) || 20,
        date: formatDateForInput(orderData.service_item_date || serviceItemDate),
        time: orderData.service_item_slot || serviceItemSlot
      };
      console.log('Setting filters from order details:', filters);
      dispatch(setCateringCartFilters(filters));
      
      // Step 1: Add the FULL package data to cart (not the partial data from order API)
      // This includes pricing_tiers, categories with choose_rule, and all other metadata
      console.log('Step 1: Adding FULL package structure to cart:', fullPackageData);
      dispatch(addCateringCartPackage(fullPackageData));
      
      // Step 2: Prepare service items data for restoration
      const detailedServiceItems = orderData.service_item || serviceItems;
      const itemsToRestore = detailedServiceItems.map(serviceItem => ({
        menu_id: serviceItem.menu_item.menu_id,
        category_id: serviceItem.category_id,
        quantity: parseInt(serviceItem.quantity) || parseInt(serviceItem.choose) || 1,
        add_on_price: parseFloat(serviceItem.add_on_price || 0)
      }));
      
      console.log('📋 Step 2: Prepared items to restore for Order #' + (orderData.order_no || orderNo) + ':', {
        orderId: orderData.id || orderId,
        orderNo: orderData.order_no || orderNo,
        itemCount: itemsToRestore.length,
        items: itemsToRestore
      });
      
      // Step 3: Store items to restore in Redux (NO session storage!)
      console.log('📋 Step 3: Setting itemsToRestore in Redux');
      dispatch(setCateringItemsToRestore(itemsToRestore));
      
      // Set flag to hide Book Slot button for pending payment orders
      if (!isModifyMode) {
        dispatch(setIsPastOrderRestoration(true));
      }
      
      console.log('Navigating to package detail with restored cart (Redux):', {
        packageId: orderData.service_item_id || serviceItemId,
        filters,
        isModifyMode,
        itemsToRestoreCount: itemsToRestore.length
      });
      
      // Navigate to package detail page
      navigate(`/catering-package/${targetPackageId}`);
    } catch (error) {
      console.error('Error restoring order:', error);
      setShowErrorModal(true);
    }
  };

  // Monitor cateringOrderDetails changes when loading
  useEffect(() => {
    if (isLoadingOrderDetails) {
      if (
        cateringOrderDetails &&
        Object.keys(cateringOrderDetails).length > 0 &&
        (cateringOrderDetails.id === orderId || cateringOrderDetails.order_no === orderNo)
      ) {
        setIsLoadingOrderDetails(false);
        
        // Handle different order statuses appropriately
        if (orderStatus === "Pending Payment") {
          handlePendingPaymentOrder();
        } else if (orderStatus === "Paid") {
          // Only paid orders should go to modify mode
          handleModifyOrder();
        } else if (orderStatus !== "Canceled") {
          // Other non-canceled orders should continue payment (restore cart)
          handlePendingPaymentOrder();
        } else {
          handleViewDetails();
        }
      }
    }
  }, [cateringOrderDetails, isLoadingOrderDetails, orderStatus, orderId, orderNo]);

  const onViewDetailsClick = async () => {
    try {
      // Check if there's a pending payment (status is "Pending") before proceeding
      if (pendingPayment && pendingPayment.status === "Pending") {
        // Show warning about pending payment
        setShowPendingPaymentWarning(true);
        return;
      }
      
      setIsLoadingOrderDetails(true);
      // Clear any stale order details before fetching new ones to avoid mismatched restoration
      dispatch(setCateringOrderDetails(null));
      
      // For "Pending Payment" orders, we need to fetch complete order details first
      if (orderStatus === "Pending Payment") {
        console.log('Fetching complete order details for pending payment order:', orderId);
        await dispatch(getCateringOrderDetailsAsync(orderId));
        // The useEffect will handle cart restoration after order details are loaded
      } else {
        // For other orders, fetch details for viewing
        await dispatch(getCateringOrderDetailsAsync(orderId));
        // The useEffect above will handle the rest of the process when cateringOrderDetails updates
      }
    } catch (error) {
      console.error("Error fetching catering order details:", error);
      setIsLoadingOrderDetails(false);
      setShowErrorModal(true);
    }
  };

  const handleConfirmPendingPaymentOverride = async () => {
    // Clear pending payment state
    dispatch(clearPendingPayment());
    
    // Close warning modal
    setShowPendingPaymentWarning(false);
    
    // Proceed with fetching order details
    try {
      setIsLoadingOrderDetails(true);
      dispatch(setCateringOrderDetails(null));
      
      if (orderStatus === "Pending Payment") {
        console.log('Fetching complete order details for pending payment order:', orderId);
        await dispatch(getCateringOrderDetailsAsync(orderId));
      } else {
        await dispatch(getCateringOrderDetailsAsync(orderId));
      }
    } catch (error) {
      console.error("Error fetching catering order details:", error);
      setIsLoadingOrderDetails(false);
      setShowErrorModal(true);
    }
  };

  const handleCancelPendingPaymentOverride = () => {
    setShowPendingPaymentWarning(false);
  };

  const handleShowDetailsModal = async (e) => {
    // Prevent event bubbling and default behavior
    e.preventDefault();
    e.stopPropagation();
    
    // Fetch detailed order info if not already available
    if (!cateringOrderDetails || cateringOrderDetails.id !== orderId) {
      try {
        setIsLoadingModalDetails(true);
        await dispatch(getCateringOrderDetailsAsync(orderId));
        setIsLoadingModalDetails(false);
      } catch (error) {
        console.error("Error fetching order details:", error);
        setIsLoadingModalDetails(false);
        setShowErrorModal(true);
        return;
      }
    }
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
  };

  // Prepare order details for modal
  const prepareOrderDetailsForModal = () => {
    // Use cateringOrderDetails if available (from API), otherwise use props
    const detailsSource = (cateringOrderDetails && cateringOrderDetails.id === orderId) 
      ? cateringOrderDetails 
      : null;

    return {
      orderNo,
      restaurant,
      serviceItemName,
      customerName,
      orderDate,
      serviceItemDate: detailsSource?.service_item_date || serviceItemDate,
      serviceItemSlot: detailsSource?.service_item_slot || serviceItemSlot,
      noOfPax: detailsSource?.no_of_pax || noOfPax,
      orderStatus,
      paymentMethod: detailsSource?.payment_method_en || detailsSource?.payment_method_ar || "",
      updatedByAgentId,
      updatedDatetime,
      serviceItems: detailsSource?.service_item || serviceItems,
      notes,
      service_item_price: detailsSource?.service_item_price || totalPrice,
      order_total_price: detailsSource?.order_total_price || totalPrice,
      total_price: totalPrice,
      currency,
      paymentProcessingFee: detailsSource?.payment_processing_fee || "",
    };
  };

  return (
    <div className="border-2 border-slate-300 rounded-lg p-3 shadow-lg w-full h-auto bg-neutral-50">
      {/* Header with improved wrapping */}
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-lg text-gray-500 font-semibold truncate max-w-[calc(100%-80px)]">{restaurant}</h2>
        {/* <span className="text-xs text-gray-500 whitespace-nowrap">{date}</span> */}
      </div>
      <div className="text-sm text-gray-600 truncate font-medium">{serviceItemName}</div>
      <div className="text-xs text-gray-600 truncate">Order #: {orderNo}</div>
      {customerName && (
        <div className="text-xs text-gray-600 truncate">Customer: {customerName}</div>
      )}
      
      {/* Order dates section */}
      <div className="mt-2 space-y-0.5">
        {orderDate && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">Placed:</span> {orderDate}
          </div>
        )}
        {serviceItemDate && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">Event:</span> {serviceItemDate}
            {serviceItemSlot && <span className="ml-1">at {serviceItemSlot}</span>}
          </div>
        )}
      </div>
      
      {/* Modified badge */}
      {isModified && (
        <div className="flex items-center gap-1 mt-2 p-1.5 bg-blue-50 rounded border border-blue-200">
          <svg className="w-3.5 h-3.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-blue-700">
              Modified by Agent #{updatedByAgentId}
            </span>
            {updatedDatetime && (
              <span className="text-xs text-blue-600">
                on {updatedDatetime}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Service Details */}
      <div className="mt-2 w-full">
        <div className="text-sm text-gray-500">
          {noOfPax} PAX • {serviceItemSlot}
        </div>
        {serviceItems.length > 0 && (
          <div className="text-xs text-gray-400 mt-1">
            {serviceItems.length} item{serviceItems.length !== 1 ? 's' : ''} selected
          </div>
        )}
      </div>

      {/* Total with constrained width */}
      <hr style={{ marginTop: "8px", borderColor: "#959FA8" }} />
      <div className="flex justify-between mt-1 w-full">
        <span className="text-sm font-semibold text-gray-500">Total</span>
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {currency} {totalPrice}
        </span>
      </div>
      <hr style={{ marginTop: "5px", borderColor: "#959FA8" }} />
      
      {/* Notes with ellipsis for overflow */}
      <div className="block text-sm font-medium text-gray-500 mt-1 mb-3 truncate">
        Notes: {notes ? notes : "N/A"}
      </div>

      {/* Responsive Action Buttons */}
      <div className="flex flex-col w-full mt-2 gap-2">
        <div className="flex justify-between w-full gap-2 flex-wrap">
          {/* Status Button */}
          <button
            className={`${getButtonStyle(
              orderStatus
            )} text-xs font-bold rounded-md px-2 h-[21px] flex-shrink-0 text-center`}
          >
            {orderStatus}
          </button>

          {/* Action Button - different text based on order status */}
          <button
            className={`text-xs font-bold rounded-md px-2 h-[21px] flex-shrink-0 text-center ${
              isLoadingOrderDetails
                ? "bg-amber-50 text-white cursor-not-allowed"
                : orderStatus === "Paid"
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : orderStatus !== "Canceled"
                ? "bg-orange-500 text-white hover:bg-orange-600"
                : "bg-amber-100 text-white hover:bg-amber-200"
            }`}
            disabled={isLoadingOrderDetails}
            onClick={
              !isLoadingOrderDetails
                ? onViewDetailsClick
                : undefined
            }
          >
            {isLoadingOrderDetails 
              ? "Loading..." 
              : orderStatus === "Paid"
              ? "View or Modify"
              : orderStatus !== "Canceled"
              ? "Continue Order"
              : "View Details"
            }
          </button>
        </div>
        
        {/* Full Details Button */}
        <button
          onClick={handleShowDetailsModal}
          disabled={isLoadingModalDetails || isLoadingOrderDetails}
          className={`w-full text-xs font-semibold rounded-md px-3 py-2 text-center border transition-colors ${
            isLoadingModalDetails || isLoadingOrderDetails
              ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
              : "bg-slate-700 text-white border-slate-700 hover:bg-slate-800 shadow-sm"
          }`}
        >
          <div className="flex items-center justify-center gap-1.5">
            {isLoadingModalDetails ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>View Full Order Details</span>
              </>
            )}
          </div>
        </button>
      </div>

      {/* Error Modal */}
      {showErrorModal && (
        <WarningModal
          onCancel={handleErrorClose}
          onProceed={handleErrorClose}
          description="Unable to load order details"
          subDescription="The catering order details could not be retrieved. Please try again later."
          confirmButtonText="OK"
          showCancelButton={false}
          showItems={false}
        />
      )}

      {/* Pending Payment Warning Modal */}
      {showPendingPaymentWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <svg className="w-6 h-6 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h3 className="text-lg font-bold text-gray-800">
                Payment Pending
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              A customer is currently completing payment for an existing order. 
              If you proceed with viewing this order (Order #{orderNo}), 
              the current pending payment will be cancelled and the cart will be reset. 
              Are you sure you want to proceed?
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleCancelPendingPaymentOverride}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPendingPaymentOverride}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                Yes, Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      <CateringOrderDetailsModal
        isOpen={showDetailsModal}
        onClose={handleCloseDetailsModal}
        orderDetails={prepareOrderDetailsForModal()}
      />
    </div>
  );
};

// PropTypes validation
CateringPastOrderCard.propTypes = {
  restaurant: PropTypes.string.isRequired,
  serviceItemName: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  orderStatus: PropTypes.string.isRequired,
  notes: PropTypes.string,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  orderNo: PropTypes.string.isRequired,
  totalPrice: PropTypes.string.isRequired,
  currency: PropTypes.string,
  // Additional props for navigation and cart restoration
  serviceItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  restaurantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  noOfPax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serviceItemDate: PropTypes.string,
  serviceItemSlot: PropTypes.string,
  serviceItems: PropTypes.array,
  customerName: PropTypes.string,
  orderDate: PropTypes.string,
  // Props for modification tracking
  updatedByAgentId: PropTypes.string,
  updatedDatetime: PropTypes.string,
};

CateringPastOrderCard.defaultProps = {
  notes: "",
  currency: "KWD",
  serviceItems: [],
  customerName: "",
  orderDate: "",
  updatedByAgentId: "",
  updatedDatetime: "",
};

export default CateringPastOrderCard;
